import { Link } from '@tanstack/react-router'
import { motion } from 'motion/react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '#/components/ui/breadcrumb'
import { RevealItem, ScrollReveal } from '#/components/common/ScrollReveal'
import { TextAnimate } from '#/components/common/TextAnimate'
import { FaMosque } from 'react-icons/fa6'
import {
  ArabesqueCorner,
  CrescentDecor,
  IslamicStarBg,
} from './ContactHeroSvgs'
import HighlightText from '#/components/common/HighlightText'

export default function ContactHeroSection() {
  return (
    <section
      className="relative w-full min-h-[70vh] flex flex-col justify-center  noir-section"
      id="contact-hero"
    >
      {/* Background decorative SVGs (CSS-animated, SSR-safe) */}
      <IslamicStarBg />
      <ArabesqueCorner />
      <CrescentDecor />

      {/* Subtle radial glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(50% 60% at 30% 50%, rgba(255,182,6,0.07) 0%, transparent 70%)',
        }}
      />

      <div className="container relative z-10 mx-auto py-36 pt-48 flex flex-col items-center gap-8 text-center">
        <ScrollReveal className="flex flex-col items-center gap-6">
          {/* Breadcrumb */}
          <RevealItem>
            <Breadcrumb>
              <BreadcrumbList className="font-semibold text-white justify-center">
                <BreadcrumbItem>
                  <BreadcrumbLink
                    asChild
                    className="text-white/70 hover:text-primary-300 transition-colors"
                  >
                    <Link to="/" className="flex items-center gap-2">
                      <FaMosque />
                      Home
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-primary-500" />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-primary-400">
                    Contact Us
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </RevealItem>

          {/* Eyebrow */}
          <RevealItem>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary-300">
              Have Questions?
            </p>
          </RevealItem>

          {/* Title — animated body + highlighted single word */}
          <RevealItem className="w-full max-w-4xl">
            <h1 className="text-5xl font-bold text-white sm:text-6xl xl:text-7xl leading-tight">
              Get in Touch{' '}
              <HighlightText
                inView
                text="with Us"
                className="bg-linear-to-r from-primary-600/60 to-primary-800/60 text-white"
              />
            </h1>
          </RevealItem>

          {/* Description */}
          <RevealItem className="max-w-2xl">
            <TextAnimate
              by="word"
              animation="fadeIn"
              duration={1.2}
              className="text-lg text-neutral-200 leading-relaxed"
            >
              Have questions or want to learn more about our programs? We're
              here to help! Reach out to us via email, phone, or visit us in
              person. We look forward to connecting with you and welcoming you
              to our community.
            </TextAnimate>
          </RevealItem>

          {/* Animated golden divider */}
          <RevealItem>
            <motion.div
              className="h-px bg-gradient-to-r from-transparent via-primary-500 to-transparent"
              initial={{ width: 0, opacity: 0 }}
              whileInView={{ width: 240, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: 'easeOut', delay: 0.5 }}
            />
          </RevealItem>
        </ScrollReveal>
      </div>

      {/* Bottom arch wave — flows into the white ContactSection below */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
        <svg
          viewBox="0 0 1440 60"
          preserveAspectRatio="none"
          className="w-full h-15 fill-white"
          aria-hidden="true"
        >
          <path d="M0,60 C360,0 1080,0 1440,60 L1440,60 L0,60 Z" />
        </svg>
      </div>
    </section>
  )
}
